// rounds/configuration types — Zod schemas + inferred types
import { z } from "zod";
// Imported as well as re-exported at the foot of this file: `RoundDraftSchema` needs the value in
// scope, and `export * from` does not provide that.
import { DocumentDraftSchema } from "./documents";
import { RoundCompetencyDraftSchema } from "./competencies";
import {
  RoundCommunicationsDraftSchema,
  emptyCommunicationsDraft,
} from "./communications";
import {
  RoundMilestonesDraftSchema,
  defaultMilestoneDrafts,
} from "./milestones";
/**
 * The round-builder wizard's draft, as one Zod-first contract.
 *
 * Shape note: the backend splits the wizard across many calls. `POST /intake-rounds/` takes only
 * the General fields and returns a DRAFT; Eligibility is one PUT; documents and questions are
 * per-row endpoints. So only General and Eligibility live in this draft — the other two are server
 * state with their own panels, and every mutation echoes the shared `expected_revision`.
 *
 * Validation messages are i18n keys in the `acquire` namespace, resolved by the field via
 * `t()` — see rules/forms.md.
 */

/** The one timezone this pass supports. The design exposes no timezone control. */
export const ROUND_TIMEZONE = "Africa/Algiers";

/**
 * Seats the form starts with. This is the DESIGN's default (stated in its helper copy), not the
 * backend's — the serializer defaults to 100 when the field is omitted. We always send it
 * explicitly, so the form's value is what lands; the mismatch is only worth knowing about if a
 * caller ever stops sending it.
 */
export const DEFAULT_CAPACITY_TARGET = 20;

/** Backend default for the Closing Soon window, mirrored so the form opens on the same value. */
export const DEFAULT_CLOSING_SOON_THRESHOLD_HOURS = 72;
export const OperatingModeSchema = z.enum(["PILOT", "LIVE"]);
export type OperatingMode = z.infer<typeof OperatingModeSchema>;

// ── Step 1 — General ──────────────────────────────────────────────────────────────────────

/**
 * Datetime values are held as `datetime-local` wall-time strings ("2027-01-10T08:00") — what
 * the control produces and what the administrator reasons about. Conversion to an ISO instant
 * against {@link ROUND_TIMEZONE} happens at the service boundary, not in the form.
 */
const LocalDateTimeSchema = z
  .string()
  .min(1, "round-create.errors.datetime-required");

export const RoundGeneralSchema = z
  .object({
    name: z.string().trim().min(1, "round-create.errors.name-required"),
    description: z.string(),
    operating_mode: OperatingModeSchema,
    opening_at: LocalDateTimeSchema,
    closing_at: LocalDateTimeSchema,
    /**
     * Whether the dates are exact or still indicative.
     *
     * Unchecked, the public preview says « dates à confirmer » instead of running a countdown —
     * which is the honest answer when a round is scheduled before its real dates are agreed. The
     * backend has carried this since before the redesign (`BooleanField(default=True)` on create
     * and on general PATCH); the old panel simply never offered it.
     */
    dates_are_definitive: z.boolean(),
    capacity_target: z
      .number()
      .int("round-create.errors.seats-integer")
      .min(1, "round-create.errors.seats-min"),
    /**
     * Hours before `closing_at` at which the round flips to CLOSING_SOON.
     *
     * ⚠ NO LONGER EDITABLE. Figma's "Planification interne" (326:24094) never showed this
     * control — Akram's call, 2026-09-10: drop it and accept the `DEFAULT_CLOSING_SOON_
     * THRESHOLD_HOURS` (72h) default for every round rather than build a UI the design doesn't
     * have. Every round therefore always sends this constant; nothing here reads it back.
     *
     * Known consequence, accepted rather than guarded against: a round shorter than 72h opens
     * ALREADY labelled "closing soon" and immediately emails a deadline reminder to every
     * applicant (`mark_due_rounds_closing_soon` flips on `now >= closing_at - threshold_hours`,
     * which has already passed at opening for a round that short). The cross-field validation
     * that used to catch this client-side is gone with the field — there is no longer a control
     * to point the error at.
     */
    closing_soon_threshold_hours: z
      .number()
      .int("round-create.errors.threshold-integer")
      .min(1, "round-create.errors.threshold-min"),
  })
  // Mirrors the backend's cross-field rule so the administrator gets it inline; the backend
  // stays authoritative and returns the same failure as `invalid_general_configuration`.
  .refine((value) => value.closing_at > value.opening_at, {
    message: "round-create.errors.closing-before-opening",
    path: ["closing_at"],
  });

export type RoundGeneral = z.infer<typeof RoundGeneralSchema>;

// ── Step 2 — Eligibility ──────────────────────────────────────────────────────────────────

/**
 * Residency, sent invisibly.
 *
 * Legacy: the concept was cut product-side and Aymen will drop these four fields, but today
 * `residency_required` is a required field on the eligibility PUT and the three descriptions are
 * mandatory whenever it is true — enforced in the serializer AND in readiness. Rather than put a
 * dead decision in front of an administrator, the panel sends a constant.
 *
 * `true` rather than `false` is Akram's call. It makes `residency_confirmed` a submission gate,
 * so **the applicant wizard must always send `residency_confirmed: true`** — without that,
 * `missing_items` keeps `residency_response_required` and submission fails with
 * `409 application_incomplete`. That obligation is recorded in `.claude/TODO-acquire.md`.
 *
 * The API exposes these descriptions on the frozen application schema and the PUBLIC round page;
 * our UI renders neither, so nothing reaches an applicant.
 */
export const RESIDENCY_REQUIRED = true;

export const RESIDENCY_DESCRIPTIONS = {
  fr: "Résidence en Algérie requise",
  ar: "الإقامة في الجزائر مطلوبة",
  en: "Algerian residency required",
} as const;

/** `ALL` requires an empty code list; `SELECTED` requires one or more unique codes. */
export const WilayaModeSchema = z.enum(["ALL", "SELECTED"]);
export type WilayaMode = z.infer<typeof WilayaModeSchema>;

export const RoundEligibilitySchema = z
  .object({
    // 0-150 mirrors the serializer's own bounds, so an out-of-range age is caught here instead
    // of coming back as a server-side field error.
    age_min: z
      .number()
      .int()
      .min(0, "round-create.errors.age-min-invalid")
      .max(150, "round-create.errors.age-min-invalid"),
    age_max: z
      .number()
      .int()
      .min(0, "round-create.errors.age-max-invalid")
      .max(150, "round-create.errors.age-max-invalid"),
    wilaya_mode: WilayaModeSchema,
    wilaya_codes: z.array(z.string()),
    /**
     * The reference-catalogue slug for the minimum level of study.
     *
     * ⚠ NOT SELECTABLE YET, and not for a frontend reason. This is a slug into the reference
     * catalogue's `EDUCATION_LEVEL` options, and no `ReferenceCatalogRelease` exists on any
     * environment — `reference-catalog.approved.json` has never been installed. The only endpoint
     * that would serve the options is round-scoped, so it needs a round that cannot be created
     * without that same manifest. Blank round-trips safely (`allow_blank`, default "").
     */
    education_minimum_code: z.string(),
    /** Ask whether the venture is registered. Per Q6 the answer FLAGS; it never excludes. */
    ask_venture_registration: z.boolean(),
    /**
     * Ask for revenue, which is what makes the threshold below meaningful.
     *
     * ⚠ NO LONGER a control in `EligibilityStep` — Figma (329:24574) shows the revenue amount
     * always visible, so `emptyRoundDraft()` now seeds new rounds with this `true`. Still a real,
     * adopted field: an already-published round keeps whatever value it was saved with, same as
     * every other field this step reads from the server.
     */
    ask_monthly_revenue: z.boolean(),
    /**
     * Revenue above which a dossier is flagged for a human, in DZD. `null` means no threshold.
     *
     * Q6's example is a 34-year-old running a registered SARL at 400 000 DA a month being
     * « très probablement pas dans le périmètre » — and *probably* is not a judgement a machine may
     * make, which is why this flags rather than filters.
     */
    revenue_flag_threshold_dzd: z.number().int().min(0).nullable(),
    /** Whether the threshold is read as monthly or annual. */
    revenue_threshold_period: z.enum(["ANNUAL", "MONTHLY"]),
  })
  .refine((value) => value.age_min <= value.age_max, {
    message: "round-create.errors.age-range-inverted",
    path: ["age_max"],
  })
  .refine(
    (value) => value.wilaya_mode === "ALL" || value.wilaya_codes.length > 0,
    {
      message: "round-create.errors.wilayas-required",
      path: ["wilaya_codes"],
    }
  );

export type RoundEligibility = z.infer<typeof RoundEligibilitySchema>;

// ── The whole draft ───────────────────────────────────────────────────────────────────────

/**
 * The wizard's client draft: only the two panels that are edited as a form.
 *
 * Documents and questions are NOT here. Both are server state saved per row — creating a round
 * seeds three documents and the questions section, so a client copy could only disagree with what
 * the server already has.
 */
export const RoundDraftSchema = z.object({
  general: RoundGeneralSchema,
  eligibility: RoundEligibilitySchema,
  /**
   * Which competencies this round evidences, and which of those are required.
   *
   * In the client draft for the same reason documents are: the round's competency config is server
   * state (`PUT /competency-framework/`) and step 1 no longer creates a round, so there is nothing
   * to write to until one exists. Persists across a reload with the rest of the draft.
   *
   * Keyed by competency `code`, so a framework republished underneath the draft — which happens on
   * every competency edit — does not orphan the selection.
   */
  /**
   * Required documents, as an EDITING BUFFER over the round's saved requirements.
   *
   * ⚠ NOT A SECOND SOURCE OF TRUTH. The server owns them; this is what the administrator is typing
   * into before it is sent. Step 2 seeds it from `GET /document-requirements/` on mount and
   * `syncDocumentsToRound` reconciles it on « Suivant », matching by the code the name slugifies to
   * — so editing a row updates it rather than creating a twin.
   *
   * It lives here rather than in step-local state so it persists with the rest of the draft: a
   * reload mid-edit keeps the rows, which is the whole reason the draft exists.
   */
  documents: z.array(DocumentDraftSchema),
  competencies: z.array(RoundCompetencyDraftSchema),
  /**
   * Step 4 — the sender identity and the eight contact points.
   *
   * In the draft like everything above it, and for the same reason: `PATCH /communications/` and
   * `PUT /template-assignments/…` both need a round, and step 1 no longer creates one.
   *
   * ⚠ AN UNTOUCHED STEP 4 IS VALID. The step is marked « optionnel » and readiness agrees — a slot
   * with no assignment is not an enabled contact point. What does NOT pass is half-done: one sender
   * address of two, or a contact point switched on with no template.
   */
  communications: RoundCommunicationsDraftSchema,
  /**
   * Step 5 — the three post-closing milestones.
   *
   * In the draft like every other panel: `PUT /milestones/` needs a round, and step 1 no longer
   * creates one.
   *
   * ⚠ THE ONLY STEP THAT IS OPTIONAL WITH NO CAVEAT. Step 4 is marked « optionnel » but can still
   * block publication half-configured; here an unset row is simply omitted from the replace payload,
   * so it does not exist and readiness has nothing to fault. Walking past this step untouched costs
   * nothing but the candidate's answer to "when will I hear back?".
   */
  milestones: RoundMilestonesDraftSchema,
});

export type RoundDraft = z.infer<typeof RoundDraftSchema>;

/** A fresh draft: the design's defaults — 20 seats, ages 18-35, all wilayas. */
export function emptyRoundDraft(): RoundDraft {
  return {
    general: {
      name: "",
      description: "",
      operating_mode: "LIVE",
      opening_at: "",
      closing_at: "",
      // Checked by default, matching the backend and the design: most rounds are scheduled with
      // real dates, and the exception is the one worth an explicit act.
      dates_are_definitive: true,
      capacity_target: DEFAULT_CAPACITY_TARGET,
      closing_soon_threshold_hours: DEFAULT_CLOSING_SOON_THRESHOLD_HOURS,
    },
    eligibility: {
      age_min: 18,
      age_max: 35,
      wilaya_mode: "ALL",
      wilaya_codes: [],
      education_minimum_code: "",
      // Registration stays a deliberate opt-in (the backend defaults it `False` too). Revenue does
      // NOT any more — Figma always shows the amount field, so a new round always asks for it; see
      // the doc comment on `ask_monthly_revenue` above.
      ask_venture_registration: false,
      ask_monthly_revenue: true,
      revenue_flag_threshold_dzd: null,
      // Matches the serializer's own default rather than picking our own.
      revenue_threshold_period: "ANNUAL",
    },
    /**
     * The eight design rows, seven of them on, none assigned a template yet.
     *
     * Templates are NOT preselected here even though the backend seeds one per slot under the
     * conventional key. Preselecting would mean the step arrives looking complete, and the
     * administrator would never see the choice — `CommunicationsStep` offers the conventional key
     * as the obvious pick instead, which keeps the act deliberate without hiding it.
     */
    communications: emptyCommunicationsDraft(),
    /**
     * The three rows, all UNSET, both branches empty.
     *
     * No dates are invented here. The design shows filled examples (2 weeks, 6 weeks, a September
     * date), but those are one round's real answers — prefilling them would put a schedule the
     * foundation never agreed to in front of every candidate.
     */
    milestones: defaultMilestoneDrafts(),
    /**
     * EMPTY, and seeded from the active framework when the step first renders.
     *
     * Unlike documents there is no sensible hardcoded default: the list is whatever the live
     * competency library holds — 24 rows today, and different after every publish. The panel seeds
     * one row per competency via `defaultRoundCompetency` on first paint.
     */
    /**
     * EMPTY, then seeded from the round on step 2's first render.
     *
     * No hardcoded defaults: `create_intake_round` already copies every active
     * `DefaultRoundDocumentRequirement` onto the round, so the two rows the design shows are
     * already there. Prefilling them here would show each requirement twice — once from the seed,
     * once from us — and the sync would then reconcile our copy against a row it matched by code.
     */
    documents: [],
    competencies: [],
  };
}

export * from "./wizard-step";
export * from "./deadline";
export * from "./history";
export * from "./documents";
export * from "./competencies";
export * from "./questions";
export * from "./readiness";
export * from "./snapshot";
export * from "./communications";
export * from "./milestones";
export * from "./required-arabic";
